import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { ProductImagesV2 } from "components/shared/ProductImages.js";
import useQcImageMetaCategories from "hooks/useQcImageMetaCategories";
import QcStatus from "pages/qc/components/QcStatus.js";
import moment from "moment";
import { Link, Navigate, useParams } from "react-router-dom";
import { FETCH_QC_REPORT } from "../graphql.js";
import FinalQcReportEditForm from "./FinalQcReportEditForm.js";

function FinalQcReportPage() {
  const { id } = useParams();
  const fromUrl = new URLSearchParams(window.location.search).get("from");
  const { loading, error, data } = useQuery(FETCH_QC_REPORT, {
    variables: { id },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const report = data.report;
  if (report.po) {
    const to = fromUrl
      ? `/qc/po-reports/${report.id}?from=${encodeURIComponent(fromUrl)}`
      : `/qc/po-reports/${report.id}`;
    return <Navigate to={to} replace />;
  }

  return <SoFinalQcReportContent report={report} fromUrl={fromUrl} />;
}

function SoFinalQcReportContent({ report, fromUrl }) {
  const metaCategoriesQuery = useQcImageMetaCategories();
  if (metaCategoriesQuery.loading) return <Spinner />;
  if (metaCategoriesQuery.error)
    return <Errors error={metaCategoriesQuery.error} />;

  const productName = report.product.productLine
    ? report.product.productLine.name
    : report.product.name;

  // BOM components list
  // If no children but product has defaultSupplier, use product itself as the only BOM element
  const bom =
    report.product.children && report.product.children.length > 0
      ? report.product.children
      : report.product.defaultSupplier
        ? [
            {
              id: `self-${report.product.id}`,
              product: report.product,
            },
          ]
        : [];

  // Use snapshot suppliers from report (created when report was generated)
  const snapshotSuppliers = report.snapshotSuppliers || [];

  return (
    <Page
      backTo={fromUrl ?? "/qc/reports"}
      title={report.name}
      subtitle={`${productName} ✕ ${report.qty}`}
      rightButtons={
        <Link to={`/qc/reports/${report.id}/preview`}>Preview 预览</Link>
      }
    >
      <div className="dark:text-gray-300 text-sm">
        <div className="flex flex-col m-4 lg:m-6 gap-4">
          <div className="flex mb-1">
            <ProductImagesV2 images={report.product.images} />
          </div>

          <div className="font-semibold text-base">
            [{report.product.number}] {report.product.name}
          </div>

          <div className="flex">
            <label className="w-24 flex-shrink-0">Suppliers: </label>
            <span className="flex-1">
              {snapshotSuppliers.length > 0
                ? snapshotSuppliers.map((s) => s.nickName || s.name).join(", ")
                : "N/A"}
            </span>
          </div>

          <div className="flex">
            <label className="w-24 flex-shrink-0">Packing: </label>
            <span className="flex-1">
              {report.factory ? report.factory.name : "N/A"}
            </span>
          </div>

          <div className="flex">
            <label className="w-24 flex-shrink-0">Created: </label>
            <span className="flex-1">
              {report.createdAt ? moment(report.createdAt).format("YYYY-MM-DD HH:mm") : "N/A"}
            </span>
          </div>

          <QcStatus status={report.status} />
        </div>

        <FinalQcReportEditForm
          metaCategories={metaCategoriesQuery.metaCategories}
          report={report}
          bom={bom}
        />
      </div>
    </Page>
  );
}

export default FinalQcReportPage;
